import { Dispatch, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { GameFeed } from './data/MLBGameFeed'
import React from 'react'
import { clamp } from './util/MathUtil'
import { PlayerControls } from './components/Players'
import { range } from 'lodash'
import { 
  BatterCard, 
  PitcherCard, 
  GameLogEntry,
  resolveAtBat,
  fetchAndSetupRosters,
  currentBatterRef,
  currentPitcherRef,
  currentInningRef,
  currentHalfRef,
  outsRef,
  scoreTeam1Ref,
  scoreTeam2Ref,
  basesRef,
  atBatTeamRef,
  gameLogEntriesRef,
  team1BatterStatsRef,
  team2BatterStatsRef,
  team1PitcherStatsRef,
  team2PitcherStatsRef,
  inningScoresRef,
  gameStatusRef,
  isLoadingRostersRef,
  rosterErrorRef
} from './simulation/StatisProEngine'
import { CameraView, CameraViewEnum, GameDataAction } from './useCreateGameData'

// Synthetic data generation for 3D visualization
function generateSyntheticPitchData(outcome: string): GameFeed.PitchData {
  const baseData = {
    startSpeed: 85 + Math.random() * 15, // 85-100 mph
    endSpeed: 80 + Math.random() * 15,
    strikeZoneTop: 3.5,
    strikeZoneBottom: 1.5,
    coordinates: {
      aY: -15 + Math.random() * 5,
      aZ: -10 + Math.random() * 5,
      pfxX: -5 + Math.random() * 10,
      pfxZ: -5 + Math.random() * 10,
      px: -1 + Math.random() * 2, // Strike zone width
      pZ: 1.5 + Math.random() * 2, // Strike zone height
      vX0: -5 + Math.random() * 10,
      vY0: -130 + Math.random() * 10,
      vZ0: -5 + Math.random() * 10,
      x: 0,
      y: 50,
      z: 0,
      x0: 0,
      y0: 50,
      z0: 6,
      aX: 0
    },
    breaks: {
      breakAngle: Math.random() * 360,
      breakLength: 5 + Math.random() * 10,
      breakY: 24,
      spinDirection: Math.random() * 360,
      spinRate: 1500 + Math.random() * 1500
    },
    zone: Math.floor(Math.random() * 14) + 1,
    typeConfidence: 0.9,
    plateTime: 0.4,
    extension: 6.5
  }

  // Adjust based on outcome
  if (outcome.includes('Strike') || outcome.includes('K')) {
    // More likely to be in strike zone
    baseData.coordinates.px = -0.8 + Math.random() * 1.6
    baseData.coordinates.pZ = 1.8 + Math.random() * 1.4
  } else if (outcome.includes('Ball') || outcome.includes('W')) {
    // More likely to be outside strike zone
    if (Math.random() > 0.5) {
      baseData.coordinates.px = Math.random() > 0.5 ? -1.5 - Math.random() : 1.5 + Math.random()
    } else {
      baseData.coordinates.pZ = Math.random() > 0.5 ? 1.0 - Math.random() : 3.8 + Math.random()
    }
  }

  return {
    ...baseData,
    breaks: {
      ...baseData.breaks,
      breakVertical: 0,
      breakVerticalInduced: 0,
      breakHorizontal: 0
    },
    coordinates: {
      ...baseData.coordinates,
      pX: baseData.coordinates.px
    }
  }
}

function generateSyntheticHitData(outcome: string): GameFeed.HitData | undefined {
  if (outcome.includes('Out') || outcome.includes('Strike') || outcome.includes('Walk')) {
    return undefined
  }

  let launchSpeed = 70 + Math.random() * 40 // 70-110 mph
  let launchAngle = -10 + Math.random() * 50 // -10 to 40 degrees
  let totalDistance = 200 + Math.random() * 200 // 200-400 feet

  // Adjust based on hit type
  if (outcome.includes('Home Run') || outcome.includes('HR')) {
    launchSpeed = 95 + Math.random() * 15 // 95-110 mph
    launchAngle = 15 + Math.random() * 25 // 15-40 degrees
    totalDistance = 350 + Math.random() * 100 // 350-450 feet
  } else if (outcome.includes('Triple') || outcome.includes('3B')) {
    launchSpeed = 85 + Math.random() * 20 // 85-105 mph
    launchAngle = 5 + Math.random() * 20 // 5-25 degrees
    totalDistance = 300 + Math.random() * 100 // 300-400 feet
  } else if (outcome.includes('Double') || outcome.includes('2B')) {
    launchSpeed = 80 + Math.random() * 20 // 80-100 mph
    launchAngle = 5 + Math.random() * 25 // 5-30 degrees
    totalDistance = 250 + Math.random() * 100 // 250-350 feet
  } else if (outcome.includes('Single') || outcome.includes('1B')) {
    launchSpeed = 70 + Math.random() * 25 // 70-95 mph
    launchAngle = -5 + Math.random() * 30 // -5 to 25 degrees
    totalDistance = 200 + Math.random() * 150 // 200-350 feet
  }

  return {
    launchSpeed,
    launchAngle,
    totalDistance,
    trajectory: GameFeed.Trajectory.FlyBall,
    hardness: launchSpeed > 95 ? GameFeed.Hardness.Hard : GameFeed.Hardness.Medium,
    location: String(Math.floor(Math.random() * 9) + 1),
    coordinates: {
      coordX: -100 + Math.random() * 200,
      coordY: 100 + Math.random() * 200
    }
  }
}

function convertStatisProToGameFeed(
  logEntry: GameLogEntry,
  batter: BatterCard,
  pitcher: PitcherCard,
  gameState: any
): { play: any, event: any } {
  const pitchData = generateSyntheticPitchData(logEntry.outcome)
  const hitData = generateSyntheticHitData(logEntry.outcome)

  // Determine event type based on outcome
  let eventType: GameFeed.EventType = GameFeed.EventType.Strikeout
  let eventDetailsCode: GameFeed.EventDetailsCode = GameFeed.EventDetailsCode.SwingingStrike

  // Convert display text for walks to proper baseball terminology
  let displayOutcome = logEntry.outcome
  if (logEntry.outcome.includes('Walk') || logEntry.outcome === 'W') {
    displayOutcome = logEntry.outcome.replace(/\bWalk\b/g, 'BB').replace(/\bW\b/g, 'BB')
  }

  if (logEntry.outcome.includes('Strikeout') || logEntry.outcome.includes('K')) {
    eventType = GameFeed.EventType.Strikeout
    eventDetailsCode = GameFeed.EventDetailsCode.SwingingStrike
  } else if (logEntry.outcome.includes('Walk') || logEntry.outcome.includes('W')) {
    eventType = GameFeed.EventType.Walk
    eventDetailsCode = GameFeed.EventDetailsCode.Ball
  } else if (logEntry.outcome.includes('Home Run') || logEntry.outcome.includes('HR')) {
    eventType = GameFeed.EventType.HomeRun
    eventDetailsCode = GameFeed.EventDetailsCode.InPlayRun
  } else if (logEntry.outcome.includes('Triple') || logEntry.outcome.includes('3B')) {
    eventType = GameFeed.EventType.Triple
    eventDetailsCode = GameFeed.EventDetailsCode.InPlayNoOut
  } else if (logEntry.outcome.includes('Double') || logEntry.outcome.includes('2B')) {
    eventType = GameFeed.EventType.Double
    eventDetailsCode = GameFeed.EventDetailsCode.InPlayNoOut
  } else if (logEntry.outcome.includes('Single') || logEntry.outcome.includes('1B')) {
    eventType = GameFeed.EventType.Single
    eventDetailsCode = GameFeed.EventDetailsCode.InPlayNoOut
  } else if (logEntry.outcome.includes('Hit By Pitch') || logEntry.outcome.includes('HBP')) {
    eventType = GameFeed.EventType.HitByPitch
    eventDetailsCode = GameFeed.EventDetailsCode.HitByPitch
  } else {
    // Default to out
    eventType = GameFeed.EventType.FieldOut
    eventDetailsCode = GameFeed.EventDetailsCode.InPlayOut
  }

  // Create synthetic runners based on current base state
  const runners: GameFeed.Runner[] = []
  
  // DEBUG: Log the conversion process to understand synthetic runner generation
  console.log('[DEBUG] Converting Statis Pro to GameFeed:', {
    outcome: logEntry.outcome,
    description: logEntry.description,
    eventType,
    batterName: batter.name,
    gameStateBases: {
      first: gameState.bases.first?.name,
      second: gameState.bases.second?.name,
      third: gameState.bases.third?.name
    }
  })
  
  // Add batter as runner
  const batterEnd = eventType === GameFeed.EventType.Single ? '1B' :
                   eventType === GameFeed.EventType.Double ? '2B' :
                   eventType === GameFeed.EventType.Triple ? '3B' :
                   eventType === GameFeed.EventType.HomeRun ? 'score' :
                   eventType === GameFeed.EventType.Walk ? '1B' :
                   eventType === GameFeed.EventType.HitByPitch ? '1B' : null
  
  const batterIsOut = [GameFeed.EventType.Strikeout, GameFeed.EventType.FieldOut].includes(eventType)
  const batterOutBase = batterIsOut ? '1B' : null
  
  console.log('[DEBUG] Batter runner movement plan:', {
    batterName: batter.name,
    start: 'null (home)',
    end: batterEnd,
    outBase: batterOutBase,
    isOut: batterIsOut,
    eventType,
    originalOutcome: logEntry.outcome
  })

  const batterRunner: GameFeed.Runner = {
    movement: {
      start: null,
      end: batterEnd,
      outBase: batterOutBase,
      isOut: batterIsOut,
      outNumber: batterIsOut ? 1 : null,
      originBase: null
    },
    details: {
      event: eventType,
      eventType: eventType,
      movementReason: GameFeed.MovementReason.RAdvPlay,
      runner: {
        id: parseInt(batter.id),
        fullName: batter.name,
        link: `/players/${batter.id}`
      },
      responsiblePitcher: {
        id: parseInt(pitcher.id),
        link: `/players/${pitcher.id}`
      },
      isScoringEvent: eventType === GameFeed.EventType.HomeRun,
      rbi: eventType === GameFeed.EventType.HomeRun,
      earned: true,
      teamUnearned: false,
      playIndex: 0
    },
    credits: []
  }

  runners.push(batterRunner)

  // Add base runners if they advance/score
  if (gameState.bases.first) {
    const firstBaseRunner: GameFeed.Runner = {
      movement: {
        originBase: '1B',
        start: '1B',
        end: eventType === GameFeed.EventType.HomeRun ? 'score' :
             [GameFeed.EventType.Triple, GameFeed.EventType.Double].includes(eventType) ? 'score' :
             eventType === GameFeed.EventType.Single ? '2B' : '1B',
        outBase: null,
        isOut: false,
        outNumber: null
      },
      details: {
        event: eventType,
        eventType: eventType,
        movementReason: GameFeed.MovementReason.RAdvPlay,
        runner: {
          id: parseInt(gameState.bases.first.id),
          fullName: gameState.bases.first.name,
          link: `/players/${gameState.bases.first.id}`
        },
        responsiblePitcher: {
          id: parseInt(pitcher.id),
          link: `/players/${pitcher.id}`
        },
        isScoringEvent: [GameFeed.EventType.HomeRun, GameFeed.EventType.Triple, GameFeed.EventType.Double].includes(eventType),
        rbi: [GameFeed.EventType.HomeRun, GameFeed.EventType.Triple, GameFeed.EventType.Double].includes(eventType),
        earned: true,
        teamUnearned: false,
        playIndex: 0
      },
      credits: []
    }
    runners.push(firstBaseRunner)
  }

  // Similar logic for second and third base runners...

  const event: any = {
    details: {
      call: { code: 'R' as const, description: GameFeed.Description.Right },
      description: logEntry.description.replace(/\bWalk\b/g, 'BB').replace(/\bW\b/g, 'BB'),
      code: eventDetailsCode,
      ballColor: GameFeed.BallColor.RGBA268619010,
      trailColor: GameFeed.TrailColor.RGBA152010110,
      isInPlay: hitData !== undefined,
      isStrike: eventDetailsCode === GameFeed.EventDetailsCode.SwingingStrike,
      isBall: eventDetailsCode === GameFeed.EventDetailsCode.Ball,
      type: { code: 'R' as const, description: GameFeed.Description.Right },
      isOut: false,
      hasReview: false
    },
    count: {
      balls: 0, // Would need to track count state
      strikes: 0,
      outs: gameState.outs
    },
    index: 0,
    startTime: new Date(),
    endTime: new Date(),
    isPitch: true,
    type: GameFeed.PlayEventType.Pitch,
    pitchData,
    hitData,
    playId: `${logEntry.inning}-${logEntry.half}-${Date.now()}`
  }

  const play: any = {
    result: {
      type: GameFeed.ResultType.AtBat,
      event: 'Single' as GameFeed.ResultEvent,
      eventType: eventType,
      description: logEntry.description.replace(/\bWalk\b/g, 'BB').replace(/\bW\b/g, 'BB'),
      rbi: eventType === GameFeed.EventType.HomeRun ? 1 : 0,
      awayScore: gameState.scoreTeam1,
      homeScore: gameState.scoreTeam2,
      isOut: [GameFeed.EventType.Strikeout, GameFeed.EventType.FieldOut].includes(eventType)
    },
    about: {
      atBatIndex: 0,
      halfInning: logEntry.half === 'Top' ? GameFeed.HalfInning.Top : GameFeed.HalfInning.Bottom,
      isTopInning: logEntry.half === 'Top',
      inning: logEntry.inning,
      startTime: new Date(),
      endTime: new Date(),
      isComplete: true,
      isScoringPlay: eventType === GameFeed.EventType.HomeRun,
      hasReview: false,
      hasOut: [GameFeed.EventType.Strikeout, GameFeed.EventType.FieldOut].includes(eventType),
      captivatingIndex: 0
    },
    count: {
      balls: 0,
      strikes: 0,
      outs: gameState.outs
    },
    matchup: {
      batter: {
        id: parseInt(batter.id),
        fullName: batter.name,
        link: `/players/${batter.id}`
      },
      batSide: { code: batter.cht.charAt(0) as 'L' | 'R', description: batter.cht.charAt(0) === 'L' ? GameFeed.Description.Left : GameFeed.Description.Right },
      pitcher: {
        id: parseInt(pitcher.id),
        fullName: pitcher.name,
        link: `/players/${pitcher.id}`
      },
      pitchHand: { code: pitcher.throws, description: pitcher.throws === 'L' ? GameFeed.Description.Left : GameFeed.Description.Right },
      postOnFirst: gameState.bases.first ? {
        id: parseInt(gameState.bases.first.id),
        fullName: gameState.bases.first.name,
        link: `/players/${gameState.bases.first.id}`
      } : undefined,
      postOnSecond: gameState.bases.second ? {
        id: parseInt(gameState.bases.second.id),
        fullName: gameState.bases.second.name,
        link: `/players/${gameState.bases.second.id}`
      } : undefined,
      postOnThird: gameState.bases.third ? {
        id: parseInt(gameState.bases.third.id),
        fullName: gameState.bases.third.name,
        link: `/players/${gameState.bases.third.id}`
      } : undefined,
      splits: {
        batter: GameFeed.Batter.VsRHP,
        pitcher: GameFeed.Pitcher.VsRHB,
        menOnBase: GameFeed.MenOnBase.Empty
      },
      batterHotColdZones: [],
      pitcherHotColdZones: []
    },
    pitchIndex: [0],
    actionIndex: [],
    runnerIndex: [0],
    runners,
    playEvents: [event],
    playEndTime: new Date(),
    atBatIndex: 0
  }

  return { play, event }
}

type StatisProGameState = {
  currentPlayIndex: number
  currentEventIndex: number
  simulatedPlays: any[]
  simulatedEvents: any[]
  gameLog: GameLogEntry[]
  isSimulating: boolean
  playSpeed: number
  freeCam: boolean
  cameraView?: CameraView
  playResultText?: string
  scoreboardImage?: HTMLImageElement
  currentEventHasCompleted: boolean
  muteEnabled: boolean
  autoPlayEnabled: boolean
  
  // UI-stable state that only updates after animations complete
  uiOuts: number
  uiInning: number
  uiHalf: 'Top' | 'Bottom'
  uiScoreTeam1: number
  uiScoreTeam2: number
  uiBases: { first: BatterCard | null; second: BatterCard | null; third: BatterCard | null }
}

type StatisProGameAction = GameDataAction | (
  { type: 'SIMULATE_NEXT_PLAY', source?: 'button_click' | 'autoplay' } |
  { type: 'INCREMENT_EVENT', increment: number } |
  { type: 'SET_PLAY_SPEED', playSpeed: number } |
  { type: 'SET_CAMERA_VIEW', cameraView: CameraView } |
  { type: 'SET_PLAY_RESULT_TEXT', text?: string } |
  { type: 'SET_FREE_CAM', freeCam: boolean } |
  { type: 'SET_SCOREBOARD_IMAGE', image?: HTMLImageElement } |
  { type: 'SET_AUTOPLAY_ENABLED', autoPlayEnabled: boolean } |
  { type: 'TOGGLE_MUTE' } |
  { type: 'SET_CURRENT_EVENT_COMPLETED' }
)

function StatisProGameReducer(state: StatisProGameState, action: StatisProGameAction): StatisProGameState {
  switch (action.type) {
    case 'SIMULATE_NEXT_PLAY': {
      console.log('************************ [STATIS PRO] SIMULATE_NEXT_PLAY triggered ************************')
      
      // Check if we can simulate - add proper guards
      if (isLoadingRostersRef.value) {
        console.log('[STATIS PRO] Cannot simulate: rosters still loading')
        return state
      }
      
      if (state.isSimulating) {
        console.log('[STATIS PRO] Cannot simulate: already simulating')
        return state
      }
      
      if (gameStatusRef.value.isGameOver) {
        console.log('[STATIS PRO] Cannot simulate: game is over')
        return state
      }
      
      const batter = currentBatterRef.value
      const pitcher = currentPitcherRef.value
      
      if (!batter || !pitcher) {
        console.error('[STATIS PRO] Cannot simulate: missing batter or pitcher')
        console.error('[STATIS PRO] Batter:', batter?.name || 'undefined')
        console.error('[STATIS PRO] Pitcher:', pitcher?.name || 'undefined')
        return state
      }
      
      // Store the PREVIOUS state before simulation (for UI stability)
      const prevOuts = outsRef.value
      const prevInning = currentInningRef.value
      const prevHalf = currentHalfRef.value
      const prevScoreTeam1 = scoreTeam1Ref.value
      const prevScoreTeam2 = scoreTeam2Ref.value
      const prevBases = {
        first: basesRef.value.first,
        second: basesRef.value.second,
        third: basesRef.value.third
      }
      
      console.log('[STATIS PRO] Pre-simulation state:')
      console.log('[STATIS PRO] - Batter:', batter.name)
      console.log('[STATIS PRO] - Pitcher:', pitcher.name)
      console.log('[STATIS PRO] - Inning:', currentInningRef.value, currentHalfRef.value)
      console.log('[STATIS PRO] - Outs:', outsRef.value)
      console.log('[STATIS PRO] - Score:', scoreTeam1Ref.value, '-', scoreTeam2Ref.value)
      
      // Set simulating flag to prevent overlapping calls
      const stateWithSimulating = { ...state, isSimulating: true }
      
      // Trigger the Statis Pro simulation
      // console.log('[STATIS PRO] Calling resolveAtBat()')
      resolveAtBat()
      
      // Get the latest game log entry
      const latestEntry = gameLogEntriesRef.value[0]
      // console.log('[STATIS PRO] Latest entry:', latestEntry)
      
      if (latestEntry) {
        const gameState = {
          outs: outsRef.value,
          scoreTeam1: scoreTeam1Ref.value,
          scoreTeam2: scoreTeam2Ref.value,
          bases: basesRef.value,
          inning: currentInningRef.value,
          half: currentHalfRef.value
        }
        
        // console.log('[STATIS PRO] Post-simulation game state:', gameState)
        const { play, event } = convertStatisProToGameFeed(latestEntry, batter, pitcher, gameState)
        // console.log('[STATIS PRO] Generated play:', play)
        // console.log('[STATIS PRO] Generated event:', event)
        
        const newPlays = [...state.simulatedPlays, play]
        const newEvents = [...state.simulatedEvents, event]
        
        const newState = {
          ...stateWithSimulating,
          simulatedPlays: newPlays,
          simulatedEvents: newEvents,
          gameLog: [...stateWithSimulating.gameLog, latestEntry],
          currentPlayIndex: newPlays.length - 1, // Point to the new play (last index)
          currentEventIndex: newEvents.length - 1, // Point to the new event (last index)
          currentEventHasCompleted: false,
          isSimulating: false, // Reset immediately for manual control - 3D animation will handle its own timing
          
          // Keep UI state FROZEN at previous values during animation
          // UI will only update after animation completes via SET_CURRENT_EVENT_COMPLETED
          uiOuts: prevOuts,
          uiInning: prevInning,
          uiHalf: prevHalf,
          uiScoreTeam1: prevScoreTeam1,
          uiScoreTeam2: prevScoreTeam2,
          uiBases: prevBases
        }
        
        // console.log('[STATIS PRO] New state created:')
        // console.log('[STATIS PRO] - Plays count:', newState.simulatedPlays.length)
        // console.log('[STATIS PRO] - Events count:', newState.simulatedEvents.length)
        console.log('[STATIS PRO] - Latest Entry:', latestEntry)
        // console.log('[STATIS PRO] - Current play index:', newState.currentPlayIndex)
        // console.log('[STATIS PRO] - Current event index:', newState.currentEventIndex)
        // console.log('[STATIS PRO] - Current event completed:', newState.currentEventHasCompleted)
        // console.log('[STATIS PRO] - UI state frozen at previous values - Outs:', newState.uiOuts, 'vs Engine Outs:', outsRef.value)
        return newState
      } else {
        console.error('[STATIS PRO] No game log entry generated after resolveAtBat()')
        return state
      }
    }
    case 'TOGGLE_MUTE': return { ...state, muteEnabled: !state.muteEnabled }
    case 'SET_AUTOPLAY_ENABLED': return { ...state, autoPlayEnabled: action.autoPlayEnabled }
    case 'SET_CURRENT_EVENT_COMPLETED': { 
      // When the animation completes, update the UI-stable state with the current engine state
      const engineOuts = outsRef.value
      const engineInning = currentInningRef.value
      const engineHalf = currentHalfRef.value
      const inningAdvanced = (state.uiInning !== engineInning) || (state.uiHalf !== engineHalf)

      // Hold only at the exact boundary when engine flips inning/half and resets outs to 0
      const shouldHold = inningAdvanced && engineOuts === 0

      // console.log('[STATIS PRO] Animation completed - updating UI state:')
      // console.log('[STATIS PRO] - Previous UI Outs:', state.uiOuts, '-> New Engine Outs:', outsRef.value)
      // console.log('[STATIS PRO] - Previous UI Score:', state.uiScoreTeam1, '-', state.uiScoreTeam2, '-> New Engine Score:', scoreTeam1Ref.value, '-', scoreTeam2Ref.value)
      
      return {
        ...state,
        currentEventHasCompleted: true,
        isSimulating: false,
        // Show 3 outs when inning just ended (engine advanced inning and reset outs to 0)
        // Otherwise sync with engine outs
        uiOuts: shouldHold ? 3 : outsRef.value,
        // Keep previous inning/half when showing 3 outs, otherwise sync with engine
        uiInning: shouldHold ? state.uiInning : currentInningRef.value,
        uiHalf: shouldHold ? state.uiHalf : currentHalfRef.value,
        uiScoreTeam1: scoreTeam1Ref.value,
        uiScoreTeam2: scoreTeam2Ref.value,
        uiBases: {
          first: basesRef.value.first,
          second: basesRef.value.second,
          third: basesRef.value.third
        }
      }
    }
    case 'SET_SCOREBOARD_IMAGE': return { ...state, scoreboardImage: action.image }
    case 'SET_CAMERA_VIEW': return { ...state, cameraView: action.cameraView }
    case 'SET_PLAY_SPEED': return { ...state, playSpeed: Math.max(0.1, action.playSpeed) }
    case 'SET_PLAY_RESULT_TEXT': return { ...state, playResultText: action.text }
    case 'SET_FREE_CAM': return { ...state, freeCam: action.freeCam }
    case 'INCREMENT_EVENT': {
      const newIndex = state.currentEventIndex + action.increment
      return {
        ...state,
        currentEventIndex: Math.max(0, Math.min(newIndex, state.simulatedEvents.length - 1)),
        currentEventHasCompleted: false
      }
    }
    default:
      return state
  }
}

const DefaultStatisProState: StatisProGameState = {
  currentPlayIndex: 0,
  currentEventIndex: 0,
  simulatedPlays: [],
  simulatedEvents: [],
  gameLog: [],
  isSimulating: false,
  playSpeed: 0.8,
  freeCam: false,
  currentEventHasCompleted: true,
  muteEnabled: false,
  autoPlayEnabled: false,  // Disabled by default for manual control
  
  // Initialize UI-stable state
  uiOuts: 0,
  uiInning: 1,
  uiHalf: 'Top',
  uiScoreTeam1: 0,
  uiScoreTeam2: 0,
  uiBases: { first: null, second: null, third: null }
}

// Global initialization tracker to prevent duplicate initializations
let globalInitialized = false

export default function useStatisProGameData(): [any, Dispatch<StatisProGameAction>] {
  const [state, dispatch] = useReducer(StatisProGameReducer, DefaultStatisProState)
  const [isMobileLayout, setIsMobileLayout] = useState(() => (window.innerWidth / window.innerHeight) <= 2/3)
  const initRef = useRef(false)

  // Reset game state on page reload
  useEffect(() => {
    // Reset the engine state variables
    outsRef.value = 0;
    currentInningRef.value = 1;
    currentHalfRef.value = 'Top';
    scoreTeam1Ref.value = 0;
    scoreTeam2Ref.value = 0;
    basesRef.value = { first: null, second: null, third: null };
    
    // Reset global initialization flag on page reload
    const handleBeforeUnload = () => {
      globalInitialized = false;
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Initialize the Statis Pro rosters and start simulation (only once)
  useEffect(() => {
    // Skip if already initialized globally or in this component instance
    if (globalInitialized || initRef.current) {
      console.log('[STATIS PRO] Already initialized globally, skipping...')
      return
    }
    
    // Mark as initialized immediately to prevent parallel initialization
    globalInitialized = true
    initRef.current = true
    
    const initializeGame = async () => {
      // console.log('[STATIS PRO] Starting roster initialization...')
      
      // Reset game state before starting
      outsRef.value = 0;
      currentInningRef.value = 1;
      currentHalfRef.value = 'Top';
      scoreTeam1Ref.value = 0;
      scoreTeam2Ref.value = 0;
      basesRef.value = { first: null, second: null, third: null };
      
      // Reset inning scores
      inningScoresRef.value = [{ inning: 1, team1Runs: 0, team2Runs: 0 }];
      
      // Clear any existing game log entries
      if (gameLogEntriesRef.value && gameLogEntriesRef.value.length > 0) {
        gameLogEntriesRef.value = [];
      }
      
      await fetchAndSetupRosters()
      // console.log('[STATIS PRO] Rosters loaded, isLoadingRosters:', isLoadingRostersRef.value)
      // console.log('[STATIS PRO] Current batter:', currentBatterRef.value?.name)
      // console.log('[STATIS PRO] Current pitcher:', currentPitcherRef.value?.name)
      
      // Only start simulation if rosters loaded successfully
      if (!isLoadingRostersRef.value && currentBatterRef.value && currentPitcherRef.value) {
        // console.log('[STATIS PRO] Starting first simulation (one-time only)...')
        // Initialize UI state before the first play
        dispatch({
          type: 'SET_CURRENT_EVENT_COMPLETED',
        });
        
        // Simulate the first play
        setTimeout(() => {
          dispatch({ type: 'SIMULATE_NEXT_PLAY' });
        }, 100);
      } else {
        console.error('[STATIS PRO] Failed to load rosters or players not available')
        // Reset initialization flags if failed
        globalInitialized = false
        initRef.current = false
      }
    }
    
    initializeGame()
  }, [])

  // Autoplay has been completely disabled - we now only use button clicks
  // Keeping this commented out for future reference
  useEffect(() => {
    if (state.autoPlayEnabled === true &&
        !gameStatusRef.value.isGameOver &&
        !isLoadingRostersRef.value &&
        state.currentEventHasCompleted &&
        state.simulatedPlays.length > 0) {
      
      const timer = setTimeout(() => {
        dispatch({ type: 'SIMULATE_NEXT_PLAY', source: 'autoplay' })
      }, 2000)
      
      return () => clearTimeout(timer)
    }
  }, [state.autoPlayEnabled, state.currentEventHasCompleted])

  const currentPlay = state.simulatedPlays[state.currentPlayIndex]
  const currentEvent = state.simulatedEvents[state.currentEventIndex]
  
  // Create synthetic linescore data
  const linescore = useMemo(() => {
    if (!currentPlay) return undefined
    
    return {
      currentInning: state.uiInning, // Use UI-stable inning
      currentInningOrdinal: `${state.uiInning}${state.uiInning === 1 ? 'st' : state.uiInning === 2 ? 'nd' : state.uiInning === 3 ? 'rd' : 'th'}`,
      inningState: state.uiHalf === 'Top' ? 'Top' : 'Bottom', // Use UI-stable half
      inningHalf: state.uiHalf === 'Top' ? 'Top' : 'Bottom', // Use UI-stable half
      scheduledInnings: 9,
      innings: inningScoresRef.value.map(inning => ({
        num: inning.inning,
        ordinalNum: `${inning.inning}${inning.inning === 1 ? 'st' : inning.inning === 2 ? 'nd' : inning.inning === 3 ? 'rd' : 'th'}`,
        home: { runs: inning.team2Runs, hits: 0, errors: 0, leftOnBase: 0 },
        away: { runs: inning.team1Runs, hits: 0, errors: 0, leftOnBase: 0 }
      })),
      teams: {
        home: {
          runs: state.uiScoreTeam2, // Use UI-stable score
          hits: 0,
          errors: 0,
          leftOnBase: 0
        },
        away: {
          runs: state.uiScoreTeam1, // Use UI-stable score
          hits: 0,
          errors: 0,
          leftOnBase: 0
        }
      },
      defense: {},
      offense: {}
    }
  }, [currentPlay, state.simulatedPlays.length])

  // Create synthetic ball landing location
  const ballLandingLocation: [number, number, number] | undefined = useMemo(() => {
    if (!currentEvent?.hitData) return undefined
    
    // Generate realistic landing coordinates based on hit data
    const angle = Math.random() * 2 * Math.PI
    const distance = (currentEvent.hitData.totalDistance || 200) * 0.3048 // Convert feet to meters
    
    return [
      Math.cos(angle) * distance * 0.7, // X coordinate
      0, // Y coordinate (ground level)
      Math.sin(angle) * distance * 0.7  // Z coordinate
    ]
  }, [currentEvent?.hitData])

  // Create synthetic runners on base - FIX for Problem #1: Use PRE-simulation state during animation
  const currrentRunnersOnBase = useMemo(() => {
    // During animation (when currentEventHasCompleted is false), use UI-stable state (PRE-simulation)
    // After animation (when currentEventHasCompleted is true), use engine state (POST-simulation)
    const shouldUsePreSimulationState = !state.currentEventHasCompleted
    
    let basesToUse
    if (shouldUsePreSimulationState) {
      // Use the UI-stable bases that represent the state BEFORE the current play
      basesToUse = state.uiBases
      console.log('[DEBUG] Using PRE-simulation state for runners (during animation)')
    } else {
      // Use the live engine state that represents the state AFTER the current play
      basesToUse = basesRef.value
      console.log('[DEBUG] Using POST-simulation state for runners (animation completed)')
    }
    
    const result = {
      '1B': basesToUse.first ? { id: parseInt(basesToUse.first.id), fullName: basesToUse.first.name, link: `/players/${basesToUse.first.id}` } : undefined,
      '2B': basesToUse.second ? { id: parseInt(basesToUse.second.id), fullName: basesToUse.second.name, link: `/players/${basesToUse.second.id}` } : undefined,
      '3B': basesToUse.third ? { id: parseInt(basesToUse.third.id), fullName: basesToUse.third.name, link: `/players/${basesToUse.third.id}` } : undefined,
      'B': currentBatterRef.value ? { id: parseInt(currentBatterRef.value.id), fullName: currentBatterRef.value.name, link: `/players/${currentBatterRef.value.id}` } : undefined,
      'score': undefined
    }
    
    console.log('[DEBUG] currrentRunnersOnBase computed:', {
      shouldUsePreSimulationState,
      currentEventCompleted: state.currentEventHasCompleted,
      uiBases: {
        first: state.uiBases.first?.name,
        second: state.uiBases.second?.name,
        third: state.uiBases.third?.name
      },
      engineBases: {
        first: basesRef.value.first?.name,
        second: basesRef.value.second?.name,
        third: basesRef.value.third?.name
      },
      basesUsed: {
        first: basesToUse.first?.name,
        second: basesToUse.second?.name,
        third: basesToUse.third?.name
      },
      result
    })
    
    return result
  }, [state.simulatedPlays.length, state.currentEventHasCompleted, state.uiBases])

  useEffect(() => {
    const resizeHandler = () => {
      setIsMobileLayout((window.innerWidth / window.innerHeight) <= 2/3)
    }
    document.addEventListener('resize', resizeHandler)
    return () => {
      document.removeEventListener('resize', resizeHandler)
    }
  }, [])

  return [{
    ...state,
    currentPlay,
    currentEvent,
    linescore,
    ballLandingLocation,
    currrentRunnersOnBase,
    isMobileLayout,
    rawData: undefined, // No raw MLB data
    playIndex: state.currentPlayIndex,
    eventIndex: state.currentEventIndex,
    isFinalEvent: state.currentEventIndex >= state.simulatedEvents.length - 1,
    isFirstEventOfInning: state.currentEventIndex === 0,
    isHalfInningFinalEvent: state.uiOuts >= 3, // Use UI-stable outs
    currentPlayHasCompleted: state.currentEventHasCompleted,
    halfInningComplete: state.uiOuts >= 3 && state.currentEventHasCompleted, // Use UI-stable outs
    prevPlay: state.simulatedPlays[state.currentPlayIndex - 1],
    prevEvent: state.simulatedEvents[state.currentEventIndex - 1],
    // Expose the latest game log entry for user-friendly play descriptions
    latestGameLogEntry: state.gameLog[state.gameLog.length - 1],
    scoreboardData: {
      currentOuts: state.uiOuts, // Use UI-stable outs instead of raw engine state
      currentBalls: 0, // Would need to track count
      currentStrikes: 0, // Would need to track count
      awayScoresByInning: inningScoresRef.value.map(i => i.team1Runs),
      homeScoresByInning: inningScoresRef.value.map(i => i.team2Runs),
      currentAwayScore: state.uiScoreTeam1, // Use UI-stable score
      currentHomeScore: state.uiScoreTeam2, // Use UI-stable score
      currentAwayHits: 0, // Would need to calculate
      currentAwayErrors: 0,
      currentAwayLOB: 0,
      currentHomeHits: 0,
      currentHomeErrors: 0,
      currentHomeLOB: 0,
      // Create stable runners on base from UI state
      bases: {
        '1B': state.uiBases.first ? { id: parseInt(state.uiBases.first.id), fullName: state.uiBases.first.name, link: `/players/${state.uiBases.first.id}` } : undefined,
        '2B': state.uiBases.second ? { id: parseInt(state.uiBases.second.id), fullName: state.uiBases.second.name, link: `/players/${state.uiBases.second.id}` } : undefined,
        '3B': state.uiBases.third ? { id: parseInt(state.uiBases.third.id), fullName: state.uiBases.third.name, link: `/players/${state.uiBases.third.id}` } : undefined,
        'B': currentBatterRef.value ? { id: parseInt(currentBatterRef.value.id), fullName: currentBatterRef.value.name, link: `/players/${currentBatterRef.value.id}` } : undefined,
        'score': undefined
      }
    }
  }, dispatch]
}

export const StatisProGameContext = React.createContext<[any, Dispatch<StatisProGameAction>]>([DefaultStatisProState, () => {}])